/* eslint-disable no-nested-ternary */
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  MODAL_SIZES,
  MODAL_VARIANTS,
  ConfirmationPrompt,
  ModalConfirmationFooter,
} from 'ui/modal';
import { selectIsJourneyGoalsFeatureFlagOn } from 'core/userContext';
import { useGraphql } from 'utils/hooks/useGraphql';
import { workflowDiffQuery } from 'api/queries/workflows';
import { Workflow } from 'modules/workflows/types';
import React, { useCallback } from 'react';
import {
  PublishedJourneyOptions,
  PublishedJourneyOptionsConsumer,
} from 'modules/journey/components/PublishedJourneyOptions';
import { VISITOR_TRACKING_FIELD } from 'modules/journey/components/PublishedJourneyOptions/publishedJourneyOptions.constants';
import { useWatch } from 'react-hook-form';
import {
  closeModal,
  publishWorkflowDraftRequest,
  getWorkflow,
  getNodes,
  getEdges,
  getWorkflowSendTilesCount,
  getJourneyCurrentConversion,
  getIsJourneyExitEventsEnabled,
} from '../../../ducks';
import { PUBLISH_JOURNEY_MODAL } from '../../../constants';

import { parseWorkflowForSaving } from '../../../utils';
import { ChangeLogResultsTable } from './ChangeLogResultsTable';
import { PublishJourneyFormProvider } from './PublishedJourneyFormProvider';
import { StyledModalBody } from './PublishJourneyModal.styles';
import { useJourneyVisitorTrackingContext } from 'modules/workflows/context/JourneyVisitorTrackingContext';

export const PUBLISH_NEW_WORKFLOW_MODAL = 'publish-new-workflow';
export const PUBLISH_EXISTING_WORKFLOW_MODAL = 'publish-existing-workflow';
export const PUBLISH_WORKFLOW_BUTTON_TEST_ID = 'publish-workflow-button';

interface PublishNewWorkflowModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  hasBeenPublished: boolean;
}

const JourneyChangeLogResultsTable = ({ workflow }: { workflow: Workflow }) => {
  const workflowToDiff = parseWorkflowForSaving(workflow);
  const { isLoading, error, data, refetch } = useGraphql(workflowDiffQuery, {
    variables: { workflow: { json: workflowToDiff } },
    fetchPolicy: 'network-only',
  });
  return (
    <ChangeLogResultsTable
      {...{
        isLoading,
        hasError: !!error,
        retryDiffQuery: () => refetch({ workflow: { json: workflowToDiff } }),
        data: data?.workflowDraftDiff,
      }}
    />
  );
};

export const PublishJourneyModalCmp = ({
  isOpen,
  hasBeenPublished,
}: PublishNewWorkflowModalProps) => {
  const [confirmationPromptType, setConfirmationPromptType] = React.useState(
    ConfirmationPrompt.NONE,
  );
  const dispatch = useDispatch();
  const workflowData = useSelector(getWorkflow);
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyExitEventsEnabled = useSelector(getIsJourneyExitEventsEnabled);
  const edges = useSelector(getEdges);
  const nodes = useSelector(getNodes);
  const hasSendTiles = useSelector(getWorkflowSendTilesCount) > 0;
  const hasConversionGoal = !!useSelector(getJourneyCurrentConversion);

  const visitorTrackingEnabled = useWatch({ name: VISITOR_TRACKING_FIELD });

  const { isLoading } = useJourneyVisitorTrackingContext();

  const workflow = { ...workflowData, nodes, edges };

  const handleCloseModal = () => dispatch(closeModal(PUBLISH_JOURNEY_MODAL));

  const onPublish = () => {
    setConfirmationPromptType(ConfirmationPrompt.CONFIRM);
  };

  const handlePublishWithVisitorTracking = useCallback(() => {
    const isVisitorTrackingEnabled = isLoading ? false : visitorTrackingEnabled;
    dispatch(
      publishWorkflowDraftRequest({
        ...workflow,
        enableVisitorTracking: isVisitorTrackingEnabled,
      } as any),
    );
  }, [visitorTrackingEnabled, workflow, dispatch, isJourneyExitEventsEnabled, isLoading]);

  const handleOnPublish = () => {
    if (isJourneyExitEventsEnabled) {
      handlePublishWithVisitorTracking();
    } else {
      dispatch(publishWorkflowDraftRequest(workflow));
    }

    // call set vistitor tracking if it's turned on.
    handleCloseModal();
  };

  const handleResume = () => {
    setConfirmationPromptType(ConfirmationPrompt.NONE);
  };

  const modalId = hasBeenPublished ? PUBLISH_EXISTING_WORKFLOW_MODAL : PUBLISH_NEW_WORKFLOW_MODAL;
  const headerText = hasBeenPublished ? 'Publish draft?' : 'Publish journey?';
  const hasConversionWithNoCampaigns = hasConversionGoal && !hasSendTiles;
  return (
    <Modal
      id={modalId}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={isJourneyGoalsEnabled ? MODAL_SIZES.MEDIUM : MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>{headerText}</ModalHeader>

      <StyledModalBody>
        <div>
          {hasBeenPublished ? (
            <JourneyChangeLogResultsTable workflow={workflow} />
          ) : (
            'This will turn the journey on, and eligible users will start receiving messages.'
          )}
        </div>
        {isJourneyExitEventsEnabled && (
          <PublishedJourneyOptions
            name={`${VISITOR_TRACKING_FIELD}`}
            publishedJourneyOptionsConsumer={PublishedJourneyOptionsConsumer.PublishJourneyModal}
          />
        )}
      </StyledModalBody>

      {isJourneyGoalsEnabled && hasConversionWithNoCampaigns ? (
        <ModalConfirmationFooter
          data-test="publish-workflow-modal"
          id="publish-workflow-modal-confirmation"
          onResume={handleResume}
          onConfirm={handleOnPublish}
          confirmButtonLabel="Publish"
          confirmLabel="Heads up: "
          confirmHelpText="This journey has a conversion but currently does not have any campaign contributing towards that conversion."
          resumeButtonLabel="Go back"
          promptType={confirmationPromptType}
          modalSize={MODAL_SIZES.MEDIUM}
        >
          <PrimaryButton onClick={onPublish} size={BUTTON_SIZES.LARGE}>
            Publish journey
          </PrimaryButton>
        </ModalConfirmationFooter>
      ) : (
        <ModalFooter>
          <PrimaryButton
            data-test={PUBLISH_WORKFLOW_BUTTON_TEST_ID}
            autoFocus
            onClick={handleOnPublish}
            size={BUTTON_SIZES.LARGE}
          >
            Publish journey
          </PrimaryButton>
        </ModalFooter>
      )}
    </Modal>
  );
};

export const PublishJourneyModal = (props: PublishNewWorkflowModalProps) => {
  return (
    <PublishJourneyFormProvider>
      <PublishJourneyModalCmp {...props} />
    </PublishJourneyFormProvider>
  );
};
